import { BridgeComponent, BridgeElement } from "@hotwired/hotwire-native-bridge";

/**
 * Hands a page's own actions to the shell, which draws them in the screen's bar. A page adds
 * one with attributes alone: the target on the web control, its title from `data-bridge-title`
 * (or an aria-label or text), `data-bridge-icon` naming a symbol, `data-bridge-primary` for the
 * accent colour and `data-bridge-slot="left"` for the other side of the bar. An ancestor with
 * `data-bridge-disabled` keeps its controls out of the bar. The shell replies with the index of
 * the button tapped and the matching web control is clicked, so its Turbo frame and method
 * still apply; the web control stays in the page, out of sight (overrides.css). Mounted on the
 * body: the set is described again whenever a control comes or goes, and withdrawn when the
 * page leaves, so the bar never shows a button the page no longer has.
 */
export default class extends BridgeComponent {
  static component = "buttons";
  static targets = ["button"];

  declare readonly buttonTargets: HTMLElement[];

  private active = false;
  private announced = false;
  private refreshQueued = false;
  private notifyBridgeOfDisconnect = () => {
    if (!this.announced) return;
    this.announced = false;
    this.send("disconnect");
  };

  connect() {
    super.connect();
    this.active = true;
    window.addEventListener("beforeunload", this.notifyBridgeOfDisconnect);
  }

  disconnect() {
    this.active = false;
    window.removeEventListener("beforeunload", this.notifyBridgeOfDisconnect);
    super.disconnect();
    this.notifyBridgeOfDisconnect();
  }

  buttonTargetConnected() {
    this.refresh();
  }

  buttonTargetDisconnected() {
    this.refresh();
  }

  /** One description per burst: targets connect one at a time. */
  refresh() {
    if (this.refreshQueued) return;
    this.refreshQueued = true;
    queueMicrotask(() => {
      this.refreshQueued = false;
      if (this.active && this.element.isConnected) this.notifyBridgeOfConnect();
    });
  }

  private notifyBridgeOfConnect() {
    const buttons = this.enabledButtons.map((element, index) => {
      const button = new BridgeElement(element);
      return {
        index,
        title: button.title,
        icon: button.bridgeAttribute("icon"),
        primary: button.bridgeAttribute("primary") === "true",
        slot: button.bridgeAttribute("slot") ?? "right",
      };
    });

    this.announced = true;
    this.send("connect", { buttons }, (message: any) => {
      new BridgeElement(this.enabledButtons[message.data.selectedIndex]).click();
    });
  }

  private get enabledButtons() {
    return this.buttonTargets.filter((element) => !element.closest("[data-bridge-disabled]"));
  }
}
